import {
  useState,
  useCallback,
  useRef,
  useEffect,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { useBrandTheme, type SidebarTheme } from '@/lib/brand-theme-provider';
import {
  generateBrandTheme,
  isContrastAA,
  THEME_PRESETS,
  type ThemeMode,
  type ThemePresetKey,
} from '@erp/config';
import {
  useCompanySettings,
  useUpdateCompanySettings,
} from '@/hooks/use-settings';
import { useStoreTheme, useUpdateStoreTheme } from '@/hooks/use-storefront';
import { useStorageService } from '@/lib/service-context';
import { Button } from '@erp/ui';
import { Input } from '@erp/ui';
import { Label } from '@erp/ui';
import { Slider } from '@erp/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@erp/ui';
import {
  Check,
  Upload,
  RotateCcw,
  Save,
  X,
  Sun,
  Moon,
  Laptop,
} from '@/components/icons';
import type { SidebarBrandDisplay } from '@erp/data-access/interfaces';

const DEFAULT_PRIMARY = '#1f6fff';
const DEFAULT_RADIUS = '0.625';

export function BrandingPage() {
  const { t } = useTranslation();
  const storageService = useStorageService();
  const {
    primaryColor,
    setPrimaryColor,
    borderRadius,
    setBorderRadius,
    mode,
    setMode,
    preset,
    setPreset,
    sidebarTheme,
    setSidebarTheme,
  } = useBrandTheme();
  const { data: settings, isLoading } = useCompanySettings();
  const updateMutation = useUpdateCompanySettings();
  const { data: storeTheme } = useStoreTheme();
  const updateStoreThemeMutation = useUpdateStoreTheme();

  const [softwareName, setSoftwareName] = useState('');
  const [previewColor, setPreviewColor] = useState(primaryColor);
  const [previewRadius, setPreviewRadius] = useState(borderRadius);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoDarkUrl, setLogoDarkUrl] = useState<string | null>(null);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [logoSmallUrl, setLogoSmallUrl] = useState<string | null>(null);
  const [sidebarBrandDisplay, setSidebarBrandDisplay] =
    useState<SidebarBrandDisplay>('logo_with_name');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoDarkInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const logoSmallInputRef = useRef<HTMLInputElement>(null);

  // Load existing values from settings
  useEffect(() => {
    if (settings) {
      setSoftwareName(settings.softwareName ?? '');
      setLogoUrl(settings.logoUrl ?? null);
      setLogoDarkUrl(settings.logoDarkUrl ?? null);
      setFaviconUrl(settings.faviconUrl ?? null);
      setLogoSmallUrl(settings.logoSmallUrl ?? null);
      setSidebarBrandDisplay(settings.sidebarBrandDisplay ?? 'logo_with_name');
      if (settings.primaryColor) {
        setPreviewColor(settings.primaryColor);
        setPrimaryColor(settings.primaryColor);
      }
      if (settings.borderRadius) {
        setPreviewRadius(settings.borderRadius);
        setBorderRadius(settings.borderRadius);
      }
    }
  }, [settings, setPrimaryColor, setBorderRadius]);

  const previewTheme = generateBrandTheme(previewColor);

  const handleColorChange = useCallback(
    (hex: string) => {
      setPreviewColor(hex);
      setPrimaryColor(hex);
    },
    [setPrimaryColor],
  );

  const handleRadiusChange = useCallback(
    (value: string) => {
      setPreviewRadius(value);
      setBorderRadius(value);
    },
    [setBorderRadius],
  );

  const handleReset = useCallback(() => {
    handleColorChange(DEFAULT_PRIMARY);
    handleRadiusChange(DEFAULT_RADIUS);
  }, [handleColorChange, handleRadiusChange]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      await updateMutation.mutateAsync({
        softwareName: softwareName || undefined,
        primaryColor: previewColor,
        borderRadius: previewRadius,
        themeMode: mode,
        themePreset: preset,
        logoUrl,
        logoDarkUrl,
        faviconUrl,
        logoSmallUrl,
        sidebarBrandDisplay,
      });
      const dashboardLogoUrl = logoUrl ?? logoDarkUrl ?? logoSmallUrl;
      await updateStoreThemeMutation.mutateAsync({
        faviconUrl,
        proFaviconUrl: faviconUrl,
        ...(dashboardLogoUrl && !storeTheme?.loginLogoUrl
          ? { loginLogoUrl: dashboardLogoUrl }
          : {}),
        ...(dashboardLogoUrl && !storeTheme?.proLoginLogoUrl
          ? { proLoginLogoUrl: dashboardLogoUrl }
          : {}),
      });
      setSaveMessage({ type: 'success', text: t('branding.saved') });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch {
      setSaveMessage({ type: 'error', text: t('branding.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (
    file: File,
    variant: 'light' | 'dark' | 'favicon' | 'small',
  ) => {
    try {
      const path = `logos/${variant}-${Date.now()}-${file.name}`;
      const url = await storageService.upload('branding', path, file);
      if (variant === 'light') setLogoUrl(url);
      else if (variant === 'dark') setLogoDarkUrl(url);
      else if (variant === 'favicon') setFaviconUrl(url);
      else if (variant === 'small') setLogoSmallUrl(url);
    } catch {
      setSaveMessage({ type: 'error', text: t('branding.saveError') });
    }
  };

  const handleRemoveLogo = (
    variant: 'light' | 'dark' | 'favicon' | 'small',
  ) => {
    if (variant === 'light') setLogoUrl(null);
    else if (variant === 'dark') setLogoDarkUrl(null);
    else if (variant === 'favicon') setFaviconUrl(null);
    else if (variant === 'small') setLogoSmallUrl(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">{t('branding.title')}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t('branding.description')}
        </p>
      </div>

      <div>
        {/* Controls */}
        <div className="space-y-6">
          {/* Software identity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('branding.identity')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="softwareName">
                  {t('branding.softwareName')}
                </Label>
                <Input
                  id="softwareName"
                  value={softwareName}
                  onChange={(e) => setSoftwareName(e.target.value)}
                  placeholder={t('branding.softwareNamePlaceholder')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('branding.softwareNameHint')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Logo Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('branding.logo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <SidebarBrandDisplaySelector
                value={sidebarBrandDisplay}
                onChange={setSidebarBrandDisplay}
                t={t}
              />
              <div className="grid gap-4 md:grid-cols-2">
                <ImageUploadControl
                  label={t('branding.logo')}
                  hint={t('branding.logoMainHint')}
                  value={logoUrl}
                  alt={t('branding.logo')}
                  inputRef={logoInputRef}
                  uploadLabel={t('branding.uploadLogo')}
                  removeLabel={t('common.delete')}
                  previewClassName="max-h-16 max-w-full object-contain"
                  onRemove={() => handleRemoveLogo('light')}
                  onPick={(file) => handleLogoUpload(file, 'light')}
                />
                <ImageUploadControl
                  label={t('branding.logoDark')}
                  hint={t('branding.logoDarkHint')}
                  value={logoDarkUrl}
                  alt={t('branding.logoDark')}
                  inputRef={logoDarkInputRef}
                  uploadLabel={t('branding.uploadLogoDark')}
                  removeLabel={t('common.delete')}
                  previewClassName="max-h-16 max-w-full object-contain"
                  previewShellClassName="bg-neutral-950"
                  onRemove={() => handleRemoveLogo('dark')}
                  onPick={(file) => handleLogoUpload(file, 'dark')}
                />
                <ImageUploadControl
                  label={t('branding.logoSmall')}
                  hint={t('branding.logoSmallHint')}
                  value={logoSmallUrl}
                  alt={t('branding.logoSmall')}
                  inputRef={logoSmallInputRef}
                  uploadLabel={t('branding.uploadLogoSmall')}
                  removeLabel={t('common.delete')}
                  previewClassName="size-10 object-contain"
                  onRemove={() => handleRemoveLogo('small')}
                  onPick={(file) => handleLogoUpload(file, 'small')}
                />
                <ImageUploadControl
                  label={t('branding.favicon')}
                  hint={t('branding.faviconHint')}
                  value={faviconUrl}
                  alt={t('branding.favicon')}
                  inputRef={faviconInputRef}
                  uploadLabel={t('branding.uploadFavicon')}
                  removeLabel={t('common.delete')}
                  previewClassName="size-10 object-contain"
                  onRemove={() => handleRemoveLogo('favicon')}
                  onPick={(file) => handleLogoUpload(file, 'favicon')}
                />
              </div>
            </CardContent>
          </Card>

          {/* Theme mode */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('branding.themeMode')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ModeSelector value={mode} onChange={setMode} t={t} />
            </CardContent>
          </Card>

          {/* Sidebar theme — light follows app theme, dark forces a dark menu */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('branding.sidebarTheme', { defaultValue: 'Thème du menu' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">
                {t('branding.sidebarThemeHint', {
                  defaultValue:
                    "Choisissez l'apparence du menu latéral : sombre (recommandé) ou clair.",
                })}
              </p>
              <SidebarThemeSelector
                value={sidebarTheme}
                onChange={setSidebarTheme}
                t={t}
              />
            </CardContent>
          </Card>

          {/* Theme preset gallery */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('branding.themePreset')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">
                {t('branding.themePresetHint')}
              </p>
              <PresetGallery value={preset} onChange={setPreset} />
            </CardContent>
          </Card>

          {/* Color Picker */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('branding.primaryColor')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <HexColorPicker
                color={previewColor}
                onChange={handleColorChange}
                style={{ width: '100%', height: 200 }}
              />
              <div className="flex items-center gap-3">
                <Label>{t('branding.currentColor')}</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono">#</span>
                  <HexColorInput
                    color={previewColor}
                    onChange={handleColorChange}
                    prefixed={false}
                    data-slot="input"
                    className="flex h-8 w-28 min-w-0 rounded-lg border border-input bg-white px-2.5 py-1 text-base outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 md:text-sm dark:bg-input/30"
                  />
                  <div
                    className="h-10 w-10 rounded-md border"
                    style={{ backgroundColor: previewColor }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={`inline-block h-3 w-3 rounded-full ${
                    isContrastAA(
                      previewTheme.light.primary,
                      previewTheme.light.primaryForeground,
                    )
                      ? 'bg-green-500'
                      : 'bg-red-500'
                  }`}
                />
                <span>
                  {t('branding.contrastAa')}{' '}
                  {isContrastAA(
                    previewTheme.light.primary,
                    previewTheme.light.primaryForeground,
                  )
                    ? t('branding.contrastOk')
                    : t('branding.contrastInsufficient')}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Border Radius */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('branding.borderRadius')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Slider
                  min={0}
                  max={1.5}
                  step={0.125}
                  value={[parseFloat(previewRadius) || 0]}
                  onValueChange={([v]) => handleRadiusChange(String(v ?? 0))}
                  className="flex-1"
                />
                <span className="w-16 text-sm font-mono">
                  {previewRadius}rem
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                t('common.loading')
              ) : saveMessage?.type === 'success' ? (
                <>
                  <Check size={16} /> {t('branding.saved')}
                </>
              ) : (
                <>
                  <Save size={16} /> {t('common.save')}
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw size={16} />
              {t('branding.resetDefault')}
            </Button>
          </div>
          {saveMessage && (
            <p
              className={`text-sm ${
                saveMessage.type === 'success'
                  ? 'text-success'
                  : 'text-destructive'
              }`}
            >
              {saveMessage.text}
            </p>
          )}
        </div>

      </div>
    </div>
  );
}

function ImageUploadControl({
  label,
  hint,
  value,
  alt,
  inputRef,
  uploadLabel,
  removeLabel,
  previewClassName,
  previewShellClassName = '',
  onPick,
  onRemove,
}: {
  label: string;
  hint: string;
  value: string | null;
  alt: string;
  inputRef: RefObject<HTMLInputElement | null>;
  uploadLabel: string;
  removeLabel: string;
  previewClassName: string;
  previewShellClassName?: string;
  onPick: (file: File) => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <div className="space-y-1">
        <Label>{label}</Label>
        <p className="text-xs leading-5 text-muted-foreground">{hint}</p>
      </div>
      <div className="flex min-h-20 flex-col gap-3 sm:flex-row sm:items-center">
        <div
          className={`relative flex min-h-16 min-w-24 items-center justify-center rounded-md border bg-background p-2 ${previewShellClassName}`}
        >
          {value ? (
            <>
              <img src={value} alt={alt} className={previewClassName} />
              <Button
                type="button"
                size="icon-xs"
                onClick={onRemove}
                className="absolute -end-1.5 -top-1.5 size-5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                aria-label={removeLabel}
              >
                <X size={12} />
              </Button>
            </>
          ) : (
            <Upload size={18} className="text-muted-foreground" />
          )}
        </div>
        <Input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPick(file);
            e.currentTarget.value = '';
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={() => inputRef.current?.click()}
        >
          <Upload size={16} />
          {uploadLabel}
        </Button>
      </div>
    </div>
  );
}

function SidebarBrandDisplaySelector({
  value,
  onChange,
  t,
}: {
  value: SidebarBrandDisplay;
  onChange: (value: SidebarBrandDisplay) => void;
  t: (key: string) => string;
}) {
  const options: {
    value: SidebarBrandDisplay;
    label: string;
    hint: string;
  }[] = [
    {
      value: 'logo_with_name',
      label: t('branding.sidebarDisplayLogoWithName'),
      hint: t('branding.sidebarDisplayLogoWithNameHint'),
    },
    {
      value: 'logo_only',
      label: t('branding.sidebarDisplayLogoOnly'),
      hint: t('branding.sidebarDisplayLogoOnlyHint'),
    },
  ];

  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-4">
      <div className="space-y-1">
        <Label>{t('branding.sidebarDisplay')}</Label>
        <p className="text-xs leading-5 text-muted-foreground">
          {t('branding.sidebarDisplayHint')}
        </p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => {
          const active = value === option.value;
          return (
            <Button
              key={option.value}
              type="button"
              variant="outline"
              onClick={() => onChange(option.value)}
              aria-pressed={active}
              className={`h-auto justify-start whitespace-normal rounded-md p-3 text-start ${
                active
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/15'
                  : ''
              }`}
            >
              <span className="grid gap-1">
                <span className="text-sm font-semibold">{option.label}</span>
                <span className="text-xs font-normal leading-5 text-muted-foreground">
                  {option.hint}
                </span>
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function ModeSelector({
  value,
  onChange,
  t,
}: {
  value: ThemeMode;
  onChange: (m: ThemeMode) => void;
  t: (key: string) => string;
}) {
  const options: { value: ThemeMode; label: string; Icon: typeof Sun }[] = [
    { value: 'light', label: t('theme.light'), Icon: Sun },
    { value: 'dark', label: t('theme.dark'), Icon: Moon },
    { value: 'system', label: t('theme.system'), Icon: Laptop },
  ];
  return (
    <div className="flex gap-2">
      {options.map(({ value: v, label, Icon }) => (
        <Button
          key={v}
          type="button"
          variant="outline"
          onClick={() => onChange(v)}
          className={`flex-1 gap-2 ${value === v ? 'border-primary bg-primary/10 text-primary hover:bg-primary/15' : ''}`}
          aria-pressed={value === v}
        >
          <Icon size={16} />
          {label}
        </Button>
      ))}
    </div>
  );
}

function SidebarThemeSelector({
  value,
  onChange,
  t,
}: {
  value: SidebarTheme;
  onChange: (s: SidebarTheme) => void;
  t: (key: string, opts?: { defaultValue?: string }) => string;
}) {
  const options: { value: SidebarTheme; label: string; Icon: typeof Sun }[] = [
    { value: 'dark', label: t('branding.sidebarThemeDark', { defaultValue: 'Sombre' }), Icon: Moon },
    { value: 'light', label: t('branding.sidebarThemeLight', { defaultValue: 'Clair' }), Icon: Sun },
  ];
  return (
    <div className="flex gap-2">
      {options.map(({ value: v, label, Icon }) => (
        <Button
          key={v}
          type="button"
          variant="outline"
          onClick={() => onChange(v)}
          className={`flex-1 gap-2 ${value === v ? 'border-primary bg-primary/10 text-primary hover:bg-primary/15' : ''}`}
          aria-pressed={value === v}
        >
          <Icon size={16} />
          {label}
        </Button>
      ))}
    </div>
  );
}

function PresetGallery({
  value,
  onChange,
}: {
  value: ThemePresetKey;
  onChange: (p: ThemePresetKey) => void;
}) {
  const keys = Object.keys(THEME_PRESETS) as ThemePresetKey[];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {keys.map((key) => {
        const p = THEME_PRESETS[key];
        const active = value === key;
        return (
          <Button
            key={key}
            type="button"
            variant="outline"
            onClick={() => onChange(key)}
            className={`group h-auto flex-col items-center gap-1.5 p-2.5 ${active ? 'border-primary ring-2 ring-primary/30' : ''}`}
            aria-pressed={active}
          >
            <span
              className="h-8 w-8 rounded-full border"
              style={{ backgroundColor: p.primaryHex }}
            />
            <span className="text-xs font-medium">{p.label}</span>
          </Button>
        );
      })}
    </div>
  );
}
